import { computed, shallowRef } from 'vue'
import {
  ApiError,
  novaApi,
  type AuthTokens,
  type MemberProfile,
  type NovaWechatLoginResponse,
  type NovaWechatPairingStatus,
  type NovaWechatPairingStatusResponse,
} from './api'

const AUTH_STORAGE_KEY = 'nova.auth.session.v1'
const WECHAT_FLOW_KEY = 'nova.auth.wechat.v1'
const REFRESH_LEEWAY_MS = 30_000
const WECHAT_FLOW_TTL_MS = 10 * 60_000
const PAIRING_ID_PATTERN = /^[0-9A-Za-z]{16,22}$/
const POLL_TOKEN_PATTERN = /^[0-9A-Za-z_-]{32,128}$/
const PNG_BASE64_PATTERN = /^[0-9A-Za-z+/]+={0,2}$/
const MAX_QR_BASE64_LENGTH = 3_000_000

export type AuthStatus = 'unknown' | 'anonymous' | 'authenticated' | 'refreshing'

export interface KeyValueStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface AuthClient {
  getWechatRedirect(redirectUri: string): Promise<string>
  novaWechatLogin(code: string, state: string): Promise<NovaWechatLoginResponse>
  getNovaWechatPairingStatus(pairingId: string, pollToken: string): Promise<NovaWechatPairingStatusResponse>
  sendNovaWechatPairingEmailCode(pairingId: string, pollToken: string, email: string): Promise<boolean>
  bindNovaWechatPairingEmail(
    pairingId: string,
    pollToken: string,
    email: string,
    code: string,
  ): Promise<NovaWechatPairingStatusResponse>
  refreshToken(refreshToken: string): Promise<AuthTokens>
  logout(accessToken: string): Promise<boolean>
  getCurrentUser(accessToken: string): Promise<MemberProfile>
}

export interface WechatPairingView {
  qrCodeDataUrl: string
  expiresAt: string
  status: 'PENDING' | 'EMAIL_REQUIRED'
}

export type WechatLoginCompletion =
  | { nextAction: 'LOGGED_IN', returnPath: string }
  | { nextAction: 'CONFIRM_ACCOUNT', returnPath: string, pairing: WechatPairingView }

export interface WechatPairingOutcome {
  status: NovaWechatPairingStatus
  returnPath?: string
}

interface StoredSession {
  version: 1
  tokens: AuthTokens
}

interface WechatFlow {
  state: string
  returnPath: string
  remember: boolean
  createdAt: number
}

interface ActiveWechatPairing extends WechatPairingView {
  pairingId: string
  pollToken: string
  returnPath: string
  remember: boolean
}

interface StoragePair {
  persistent?: KeyValueStorage
  session?: KeyValueStorage
}

function browserStorage(): StoragePair {
  if (typeof window === 'undefined') return {}
  return { persistent: window.localStorage, session: window.sessionStorage }
}

export function parseExpiresTime(value: AuthTokens['expiresTime']): number {
  if (typeof value === 'number') return value < 1_000_000_000_000 ? value * 1000 : value
  if (/^\d+$/.test(value)) {
    const numeric = Number(value)
    return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric
  }
  return Date.parse(value)
}

function validTokens(value: unknown): value is AuthTokens {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<AuthTokens>
  return Number.isFinite(candidate.userId)
    && typeof candidate.accessToken === 'string' && candidate.accessToken.length > 0
    && typeof candidate.refreshToken === 'string' && candidate.refreshToken.length > 0
    && Number.isFinite(parseExpiresTime(candidate.expiresTime ?? ''))
}

function invalidWechatResponse(): never {
  throw new ApiError('微信登录返回格式不正确', 'INVALID_RESPONSE')
}

function normalizePairingExpiry(value: unknown): string {
  const timestamp = typeof value === 'number'
    ? (value < 1_000_000_000_000 ? value * 1000 : value)
    : typeof value === 'string' ? Date.parse(value) : Number.NaN
  if (!Number.isFinite(timestamp)) return invalidWechatResponse()
  return new Date(timestamp).toISOString()
}

function miniProgramCodeDataUrl(value: unknown): string {
  if (typeof value !== 'string'
    || value.length < 16
    || value.length > MAX_QR_BASE64_LENGTH
    || value.length % 4 !== 0
    || !value.startsWith('iVBORw0KGgo')
    || !PNG_BASE64_PATTERN.test(value)) {
    return invalidWechatResponse()
  }
  return `data:image/png;base64,${value}`
}

function validatePairingStatusResponse(value: unknown): NovaWechatPairingStatusResponse {
  if (!value || typeof value !== 'object') return invalidWechatResponse()
  const response = value as Partial<NovaWechatPairingStatusResponse>
  const statuses: NovaWechatPairingStatus[] = ['PENDING', 'EMAIL_REQUIRED', 'CANCELLED', 'EXPIRED', 'LOGGED_IN']
  if (!response.status || !statuses.includes(response.status)) return invalidWechatResponse()
  let expiresAt: string | undefined
  if (response.status === 'PENDING' || response.status === 'EMAIL_REQUIRED') {
    expiresAt = normalizePairingExpiry(response.expiresAt)
  }
  if (response.status !== 'LOGGED_IN' && response.login != null) return invalidWechatResponse()
  if (response.status === 'LOGGED_IN' && !validTokens(response.login)) return invalidWechatResponse()
  return { ...response, ...(expiresAt ? { expiresAt } : {}) } as NovaWechatPairingStatusResponse
}

export function safeReturnPath(value: unknown, fallback = '/account'): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return fallback
  try {
    const parsed = new URL(value, 'https://nova.local')
    if (parsed.origin !== 'https://nova.local') return fallback
    if (['/login', '/register', '/forgot-password', '/auth/wechat/callback'].includes(parsed.pathname)) return fallback
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return fallback
  }
}

export function formatAuthError(error: unknown, fallback = '操作失败，请稍后重试') {
  return error instanceof Error && error.message ? error.message : fallback
}

function unauthorized(error: unknown) {
  return error instanceof ApiError && error.code === 401
}

function transientAuthError(error: unknown) {
  return error instanceof ApiError
    && (error.code === 'NETWORK' || error.code === 'INVALID_RESPONSE' || (typeof error.code === 'number' && error.code >= 500))
}

function parseStored(raw: string | null): AuthTokens | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredSession
    return parsed.version === 1 && validTokens(parsed.tokens) ? parsed.tokens : null
  } catch {
    return null
  }
}

export function createAuthSession(client: AuthClient = novaApi, providedStorage?: StoragePair) {
  const status = shallowRef<AuthStatus>('unknown')
  const tokens = shallowRef<AuthTokens | null>(null)
  const user = shallowRef<MemberProfile | null>(null)
  const wechatPairing = shallowRef<WechatPairingView | null>(null)
  let remember = false
  let initializePromise: Promise<void> | null = null
  let refreshPromise: Promise<string> | null = null
  let sessionGeneration = 0
  let pairingGeneration = 0
  let activeWechatPairing: ActiveWechatPairing | null = null

  const stores = () => providedStorage ?? browserStorage()

  const clearWechatPairing = () => {
    pairingGeneration += 1
    activeWechatPairing = null
    wechatPairing.value = null
  }

  const publishWechatPairing = (pairing: ActiveWechatPairing) => {
    wechatPairing.value = {
      qrCodeDataUrl: pairing.qrCodeDataUrl,
      expiresAt: pairing.expiresAt,
      status: pairing.status,
    }
  }

  const clear = () => {
    sessionGeneration += 1
    clearWechatPairing()
    stores().persistent?.removeItem(AUTH_STORAGE_KEY)
    stores().session?.removeItem(AUTH_STORAGE_KEY)
    stores().session?.removeItem(WECHAT_FLOW_KEY)
    tokens.value = null
    user.value = null
    remember = false
    status.value = 'anonymous'
  }

  const persist = (nextTokens: AuthTokens, keepSignedIn: boolean) => {
    if (!validTokens(nextTokens)) throw new ApiError('登录凭证格式不正确', 'INVALID_RESPONSE')
    sessionGeneration += 1
    const target = keepSignedIn ? stores().persistent : stores().session
    const other = keepSignedIn ? stores().session : stores().persistent
    other?.removeItem(AUTH_STORAGE_KEY)
    target?.setItem(AUTH_STORAGE_KEY, JSON.stringify({ version: 1, tokens: nextTokens } satisfies StoredSession))
    tokens.value = nextTokens
    remember = keepSignedIn
    status.value = 'authenticated'
  }

  const loadStored = () => {
    const persistent = parseStored(stores().persistent?.getItem(AUTH_STORAGE_KEY) ?? null)
    if (persistent) return { tokens: persistent, remember: true }
    const session = parseStored(stores().session?.getItem(AUTH_STORAGE_KEY) ?? null)
    return session ? { tokens: session, remember: false } : null
  }

  const refresh = async () => {
    if (refreshPromise) return refreshPromise
    if (!tokens.value?.refreshToken) {
      clear()
      throw new ApiError('登录状态已失效，请重新登录', 401)
    }
    const generation = sessionGeneration
    const currentRefreshToken = tokens.value.refreshToken
    status.value = 'refreshing'
    refreshPromise = client.refreshToken(currentRefreshToken)
      .then((nextTokens) => {
        if (generation !== sessionGeneration) throw new ApiError('登录状态已变更', 401)
        persist(nextTokens, remember)
        return nextTokens.accessToken
      })
      .catch((error) => {
        if (generation !== sessionGeneration) throw error
        if (transientAuthError(error) && tokens.value) status.value = 'authenticated'
        else clear()
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
    return refreshPromise
  }

  const validAccessToken = async () => {
    if (!tokens.value) throw new ApiError('请先登录', 401)
    const expiresAt = parseExpiresTime(tokens.value.expiresTime)
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now() + REFRESH_LEEWAY_MS) return refresh()
    return tokens.value.accessToken
  }

  const loadProfile = async () => {
    let accessToken = await validAccessToken()
    try {
      user.value = await client.getCurrentUser(accessToken)
    } catch (error) {
      if (!unauthorized(error)) throw error
      accessToken = await refresh()
      user.value = await client.getCurrentUser(accessToken)
    }
  }

  const initialize = () => {
    if (initializePromise) return initializePromise
    initializePromise = (async () => {
      const stored = loadStored()
      if (!stored) {
        status.value = 'anonymous'
        return
      }
      tokens.value = stored.tokens
      remember = stored.remember
      status.value = 'authenticated'
      try {
        await loadProfile()
      } catch (error) {
        if (tokens.value && !unauthorized(error)) {
          user.value = null
          status.value = 'authenticated'
        } else {
          clear()
        }
      }
    })()
    return initializePromise
  }

  const establish = async (nextTokens: AuthTokens, keepSignedIn: boolean) => {
    persist(nextTokens, keepSignedIn)
    try {
      await loadProfile()
    } catch (error) {
      if (unauthorized(error)) {
        clear()
        throw error
      }
      user.value = null
    }
  }

  const beginWechatLogin = async (redirectUri: string, returnPath: string, keepSignedIn: boolean) => {
    clearWechatPairing()
    const generation = pairingGeneration
    stores().session?.removeItem(WECHAT_FLOW_KEY)
    const authorizeUrl = await client.getWechatRedirect(redirectUri)
    if (generation !== pairingGeneration) throw new ApiError('微信登录状态已变更', 401)
    let parsed: URL
    try {
      parsed = new URL(authorizeUrl)
    } catch {
      throw new ApiError('微信授权地址格式不正确', 'INVALID_RESPONSE')
    }
    if (parsed.protocol !== 'https:' || parsed.hostname !== 'open.weixin.qq.com'
      || parsed.pathname !== '/connect/qrconnect' || parsed.username || parsed.password || parsed.port) {
      throw new ApiError('微信授权地址未通过安全校验', 'INVALID_RESPONSE')
    }
    const state = parsed.searchParams.get('state')
    const actualRedirectUri = parsed.searchParams.get('redirect_uri')
    if (!state || !parsed.searchParams.get('appid') || !actualRedirectUri) {
      throw new ApiError('微信授权地址缺少必要参数', 'INVALID_RESPONSE')
    }
    let expectedRedirect: URL
    let actualRedirect: URL
    try {
      expectedRedirect = new URL(redirectUri)
      actualRedirect = new URL(actualRedirectUri)
    } catch {
      throw new ApiError('微信回调地址格式不正确', 'INVALID_RESPONSE')
    }
    if (actualRedirect.toString() !== expectedRedirect.toString()) {
      throw new ApiError('微信回调地址未通过安全校验', 'INVALID_RESPONSE')
    }
    const flow: WechatFlow = { state, returnPath: safeReturnPath(returnPath), remember: keepSignedIn, createdAt: Date.now() }
    stores().session?.setItem(WECHAT_FLOW_KEY, JSON.stringify(flow))
    parsed.searchParams.set('login_type', 'jssdk')
    parsed.searchParams.set('self_redirect', 'false')
    return parsed.toString()
  }

  const readWechatFlow = (state: string) => {
    const raw = stores().session?.getItem(WECHAT_FLOW_KEY)
    if (!raw) throw new ApiError('微信登录请求已失效，请重新发起', 401)
    let flow: WechatFlow
    try {
      flow = JSON.parse(raw) as WechatFlow
    } catch {
      stores().session?.removeItem(WECHAT_FLOW_KEY)
      throw new ApiError('微信登录请求无法校验，请重新发起', 'INVALID_RESPONSE')
    }
    const age = Date.now() - flow.createdAt
    if (!flow.state || typeof flow.returnPath !== 'string' || typeof flow.remember !== 'boolean'
      || !Number.isFinite(flow.createdAt) || age < -60_000 || age > WECHAT_FLOW_TTL_MS) {
      stores().session?.removeItem(WECHAT_FLOW_KEY)
      throw new ApiError('微信登录请求已失效，请重新发起', 401)
    }
    if (flow.state !== state) {
      throw new ApiError('微信登录校验失败，请重新发起', 401)
    }
    return flow
  }

  const finishWechatLogin = async (code: string, state: string): Promise<WechatLoginCompletion> => {
    const flow = readWechatFlow(state)
    const generation = pairingGeneration
    const response = await client.novaWechatLogin(code, state)
    if (generation !== pairingGeneration) throw new ApiError('微信登录状态已变更', 401)
    if (!response || typeof response !== 'object') return invalidWechatResponse()

    if (response.nextAction === 'LOGGED_IN') {
      if (!validTokens(response.login)
        || response.pairingId != null
        || response.pollToken != null
        || response.miniProgramCodeBase64 != null
        || response.expiresAt != null) {
        return invalidWechatResponse()
      }
      await establish(response.login, flow.remember)
      stores().session?.removeItem(WECHAT_FLOW_KEY)
      clearWechatPairing()
      return { nextAction: 'LOGGED_IN', returnPath: safeReturnPath(flow.returnPath) }
    }

    const expiresAt = response.nextAction === 'CONFIRM_ACCOUNT'
      ? normalizePairingExpiry(response.expiresAt)
      : undefined
    if (response.nextAction !== 'CONFIRM_ACCOUNT'
      || response.login != null
      || typeof response.pairingId !== 'string'
      || !PAIRING_ID_PATTERN.test(response.pairingId)
      || typeof response.pollToken !== 'string'
      || !POLL_TOKEN_PATTERN.test(response.pollToken)) {
      return invalidWechatResponse()
    }

    const pairing: ActiveWechatPairing = {
      pairingId: response.pairingId,
      pollToken: response.pollToken,
      qrCodeDataUrl: miniProgramCodeDataUrl(response.miniProgramCodeBase64),
      expiresAt: expiresAt!,
      status: 'PENDING',
      returnPath: safeReturnPath(flow.returnPath),
      remember: flow.remember,
    }
    pairingGeneration += 1
    activeWechatPairing = pairing
    publishWechatPairing(pairing)
    stores().session?.removeItem(WECHAT_FLOW_KEY)
    return {
      nextAction: 'CONFIRM_ACCOUNT',
      returnPath: pairing.returnPath,
      pairing: wechatPairing.value!,
    }
  }

  const requireWechatPairing = () => {
    if (!activeWechatPairing) throw new ApiError('微信登录确认已失效，请重新扫码', 401)
    return activeWechatPairing
  }

  const applyWechatPairingStatus = async (
    pairing: ActiveWechatPairing,
    generation: number,
    rawResponse: NovaWechatPairingStatusResponse,
  ): Promise<WechatPairingOutcome> => {
    if (generation !== pairingGeneration || activeWechatPairing !== pairing) {
      throw new ApiError('微信登录状态已变更', 401)
    }
    const response = validatePairingStatusResponse(rawResponse)
    if (response.status === 'PENDING' || response.status === 'EMAIL_REQUIRED') {
      pairing.status = response.status
      pairing.expiresAt = normalizePairingExpiry(response.expiresAt)
      publishWechatPairing(pairing)
      return { status: response.status }
    }
    if (response.status === 'LOGGED_IN') {
      await establish(response.login!, pairing.remember)
      const returnPath = pairing.returnPath
      clearWechatPairing()
      return { status: 'LOGGED_IN', returnPath }
    }
    clearWechatPairing()
    return { status: response.status }
  }

  const pollWechatPairing = async () => {
    const pairing = requireWechatPairing()
    const generation = pairingGeneration
    const response = await client.getNovaWechatPairingStatus(pairing.pairingId, pairing.pollToken)
    return applyWechatPairingStatus(pairing, generation, response)
  }

  const sendWechatPairingEmailCode = async (email: string) => {
    const pairing = requireWechatPairing()
    const generation = pairingGeneration
    const normalizedEmail = email.trim()
    if (!normalizedEmail || normalizedEmail.length > 254) {
      throw new ApiError('请输入正确的邮箱地址', 'INVALID_RESPONSE')
    }
    const sent = await client.sendNovaWechatPairingEmailCode(
      pairing.pairingId,
      pairing.pollToken,
      normalizedEmail,
    )
    if (sent !== true) return invalidWechatResponse()
    if (generation !== pairingGeneration || activeWechatPairing !== pairing) {
      throw new ApiError('微信登录状态已变更', 401)
    }
    pairing.status = 'EMAIL_REQUIRED'
    publishWechatPairing(pairing)
  }

  const bindWechatPairingEmail = async (email: string, code: string) => {
    const pairing = requireWechatPairing()
    const generation = pairingGeneration
    const normalizedEmail = email.trim()
    const normalizedCode = code.trim()
    if (!normalizedEmail || normalizedEmail.length > 254) {
      throw new ApiError('请输入正确的邮箱地址', 'INVALID_RESPONSE')
    }
    if (!/^\d{6}$/.test(normalizedCode)) {
      throw new ApiError('请输入 6 位邮箱验证码', 'INVALID_RESPONSE')
    }
    const response = await client.bindNovaWechatPairingEmail(
      pairing.pairingId,
      pairing.pollToken,
      normalizedEmail,
      normalizedCode,
    )
    return applyWechatPairingStatus(pairing, generation, response)
  }

  const resetWechatLogin = () => {
    clearWechatPairing()
    stores().session?.removeItem(WECHAT_FLOW_KEY)
  }

  const logout = async () => {
    const accessToken = tokens.value?.accessToken
    clear()
    if (accessToken) await client.logout(accessToken).catch(() => undefined)
  }

  return {
    status,
    tokens,
    user,
    wechatPairing,
    isAuthenticated: computed(() => status.value === 'authenticated' || status.value === 'refreshing'),
    initialize,
    validAccessToken,
    beginWechatLogin,
    finishWechatLogin,
    pollWechatPairing,
    sendWechatPairingEmailCode,
    bindWechatPairingEmail,
    resetWechatLogin,
    logout,
    clear,
  }
}

export const authSession = createAuthSession()
